import { Box } from '@jfs/ui-new'
import { compareArray } from '@jfs/utils'
import { FC, useCallback, useState } from 'react'
import { TableComponent } from '../TableComponent'
import DayView from './DayView'
import styles from './styles'
import { Day, MonthProps } from './types'

const mMonthDaysCount: Array<number> = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

const createDay = (
  key: string,
  d: number,
  m: number,
  y: number,
  isEnabled = true,
  isHoliday = false,
  isSelected = false,
  isToday = false,
  isPreSelected = false,
): Day =>
  ({
    key,
    d,
    m,
    y,
    isEnabled,
    isHoliday,
    isSelected,
    isToday,
    isPreSelected,
  } as Day)

const initMonth = (
  month: number,
  year: number,
  disableAfter: number | undefined,
  disableBefore: number | undefined,
  holidayList: Array<number> | undefined,
  preSelectedDate: number,
  day: Array<Day>,
  setDays: (days: Array<Day>) => void,
  secondSelectedDate: number,
  maxDuration?: number,
): void => {
  const days = day
  days.length = 0
  let totalDays: number
  if (month === 1) {
    const isLeap = year % 100 === 0 ? year % 400 === 0 : year % 4 === 0
    totalDays = isLeap ? 29 : 28
  } else {
    totalDays = mMonthDaysCount[month]
  }
  const startDay = new Date(`${month + 1}/1/${year}`)
  const dayIndex = startDay.getDay()
  let blankIndex = 0
  for (let d1 = 0; d1 < dayIndex; d1 += 1) {
    days.push(createDay(`blank${blankIndex}`, 0, month, year, false))
    blankIndex += 1
  }
  for (let num = 1; num <= totalDays; num += 1) {
    days.push(createDay(num.toString(), num, month, year))
  }
  const spaceToAppend = days.length % 7 ? 7 - (days.length % 7) : 0
  for (let i = 0; i < spaceToAppend; i += 1) {
    days.push(createDay(`blank${blankIndex}`, 0, month, year, false))
    blankIndex += 1
  }
  if (disableBefore) {
    const lastdisabled = dayIndex + disableBefore - 2
    for (let i = dayIndex; i <= lastdisabled; i += 1) {
      days[i].isEnabled = false
    }
  }
  if (disableAfter && disableAfter > 0) {
    const firstDisabled = dayIndex + disableAfter
    const lastdisabled = dayIndex + totalDays - 1
    for (let i = firstDisabled; i <= lastdisabled; i += 1) {
      days[i].isEnabled = false
    }
  }
  if (holidayList && holidayList.length > 0) {
    holidayList.forEach((item) => {
      days[item + dayIndex - 1].isHoliday = true
    })
  }

  if (preSelectedDate !== -1) {
    if (secondSelectedDate !== -1 && maxDuration) {
      const date1 = new Date(year, month + 1, preSelectedDate).getTime()
      const date2 = new Date(year, month + 1, secondSelectedDate).getTime()
      const duration = (date1 - date2) / (24 * 60 * 60 * 1000)
      days[preSelectedDate + dayIndex - 1].isPreSelected = Math.abs(duration) <= maxDuration
    } else {
      days[preSelectedDate + dayIndex - 1].isPreSelected = true
    }
  }

  if (secondSelectedDate !== -1) {
    if (maxDuration) {
      const date1 = new Date(year, month + 1, preSelectedDate).getTime()
      const date2 = new Date(year, month + 1, secondSelectedDate).getTime()
      const duration = (date1 - date2) / (24 * 60 * 60 * 1000)
      days[secondSelectedDate + dayIndex - 1].isPreSelected = Math.abs(duration) <= maxDuration
    } else {
      days[secondSelectedDate + dayIndex - 1].isPreSelected = true
    }
  }

  setDays(days)
}

const MonthView: FC<MonthProps> = ({
  type,
  year,
  month,
  onDateSelected,
  extraData,
  disableAfter,
  disableBefore,
  holidayList = [],
  preSelectedDate = -1,
  selectedDate,
  onHover,
  hoverFrom,
  hoverTo,
  secondSelectedDate = -1,
  maxDuration,
}: MonthProps) => {
  const [days, setDays] = useState<Array<Day>>([])
  const [cMonth, setMonth] = useState(-1)
  const [cYear, setYear] = useState(-1)
  const [cDisableBefore, setDisableBefore] = useState(-1)
  const [cDisableAfter, setDisableAfter] = useState(-1)
  const [cPreselected, setPreselected] = useState(-1)
  const [cHolidayList, setHolidayList] = useState([] as Array<number>)
  const [cSecondSelectedDate, setSecondSelectedDate] = useState(-1)

  if (
    secondSelectedDate !== cSecondSelectedDate ||
    days.length === 0 ||
    month !== cMonth ||
    year !== cYear ||
    cDisableBefore !== disableBefore ||
    cDisableAfter !== disableAfter ||
    cPreselected !== preSelectedDate ||
    !compareArray(holidayList, cHolidayList)
  ) {
    initMonth(
      month,
      year,
      disableAfter,
      disableBefore,
      holidayList,
      preSelectedDate,
      days,
      setDays,
      secondSelectedDate,
      maxDuration,
    )
    setMonth(month)
    setYear(year)
    setDisableBefore(disableBefore !== undefined ? disableBefore : -1)
    setDisableAfter(disableAfter !== undefined ? disableAfter : -1)
    setPreselected(preSelectedDate)
    setHolidayList(holidayList)
    setSecondSelectedDate(secondSelectedDate)
  }
  const onDayHover = (hoveredDay): void => {
    setHoveredDay(hoveredDay)
  }

  const dayOnPress = useCallback(
    (day: Day) => {
      if (day.d <= 0 || !day.isEnabled || day.isSelected) {
        return
      }

      if (preSelectedDate > 0) {
        onDateSelected(day)
        return
      }

      if (onDateSelected !== undefined) {
        onDateSelected(day)
      }
    },
    [onDateSelected, preSelectedDate],
  )
  const renderItem = ({ item: day }: { item: Day }): JSX.Element => (
    <Box {...styles[type].DateContainer}>
      <DayView
        type={type}
        day={day}
        onPress={(): void => {
          dayOnPress(day)
        }}
        firstDate={selectedDate}
        onHover={onDayHover}
        hoverFrom={hoverFrom}
        hoverTo={hoverTo}
      />
    </Box>
  )

  const keyExtractor = useCallback((item) => {
    return item.key
  }, [])
  const setHoveredDay = useCallback((day) => onHover(day), [onHover])
  return (
    <Box paddingLeft={24} paddingRight={24}>
      <TableComponent
        value={''} //Routes value
        canFetchMore={false}
        hide={false}
        isCustom={true}
        ListContent={days ?? []}
        customStyle={styles[type].DateListContainer}
        extraData={extraData}
        keyExtractor={keyExtractor}
        RenderCustomElement={renderItem}
        overScrollMode="never"
        numColumns={7}
      />
    </Box>
  )
}

export default MonthView
